/**
 * Comms data (audio-direction §5): the garbled-voice formant synthesizer (voices, phonemes, prosody, stress) and
 * the radio chain. `comms.json` validated with Zod; mutable so the audition page can tune it.
 */
import { z } from 'zod'
import commsJson from './comms.json'

const range = (min: number, max: number): z.ZodType<[number, number]> =>
  z
    .tuple([z.number().min(min).max(max), z.number().min(min).max(max)])
    .refine((r) => r[0] <= r[1], { message: 'range must be [min, max]' })
const hz = z.number().positive().max(20000)
const formant3 = z.tuple([hz, hz, hz])

export const commsSchema = z.object({
  voice: z.object({
    femaleChance: z.number().min(0).max(1),
    pitchMale: range(40, 400),
    pitchFemale: range(40, 400),
    /** Vocal-tract scale (formants × this): women and smaller speakers read higher. */
    tractMale: range(0.5, 2),
    tractFemale: range(0.5, 2),
    /** Syllables per second at stress 0. */
    rate: range(1, 12),
    /** Aspiration noise mixed into the voicing. */
    breath: range(0, 1),
    /** Pitch jitter (fraction). */
    jitter: range(0, 0.2),
    /** Intonation range (fraction of the base pitch). */
    pitchRange: range(0, 1),
    /** Glottal spectral tilt exponent: harmonic n has amplitude 1 / n^tilt. */
    tilt: range(0.5, 3),
    /** How strongly a speaker prefers some vowels (an accent): 0 = uniform. */
    vowelBias: z.number().min(0).max(10),
  }),
  /** Adult vowel formants F1–F3 (Hz, Peterson & Barney averages), before the speaker's tract scale. */
  vowels: z.record(z.string(), formant3).refine((v) => Object.keys(v).length >= 3, { message: 'need at least 3 vowels' }),
  formants: z.object({ bandwidths: formant3, gains: z.tuple([z.number(), z.number(), z.number()]), transition: z.number().positive().max(0.5) }),
  consonants: z.object({
    plosives: z.array(z.object({ name: z.string(), hz, q: z.number().positive(), voiced: z.boolean() })).min(1),
    fricatives: z.array(z.object({ name: z.string(), hz, q: z.number().positive(), gain: z.number().min(0).max(2) })).min(1),
    nasals: z.array(z.object({ name: z.string(), f1: hz, f2: hz, f3: hz })).min(1),
    liquids: z.array(z.object({ name: z.string(), f1: hz, f2: hz, f3: hz })).min(1),
    closure: range(0, 1),
    burst: range(0, 1),
    fricative: range(0, 1),
    sonorant: range(0, 1),
  }),
  syllables: z.object({
    shapes: z.array(z.object({ shape: z.enum(['CV', 'CVC', 'V', 'CCV']), weight: z.number().positive() })).min(1),
    classes: z.array(z.object({ cls: z.enum(['plosive', 'fricative', 'nasal', 'liquid']), weight: z.number().positive() })).min(1),
    /** Vowel length at 5 syllables per second. */
    vowel: range(0.01, 1),
  }),
  prosody: z.object({
    wordsPerPhrase: range(1, 12),
    syllablesPerWord: range(1, 6),
    phrases: range(1, 6),
    wordGap: range(0, 1),
    phraseGap: range(0, 2),
    /** Pitch falls by this fraction across a phrase. */
    declination: z.number().min(0).max(1),
    stressPitch: z.number().min(0).max(1),
    stressLength: z.number().min(1).max(3),
    questionChance: z.number().min(0).max(1),
    finalFall: z.number().min(0).max(1),
    vibratoHz: z.number().min(0).max(20),
    vibratoDepth: z.number().min(0).max(0.2),
    /** Points per second of the pitch, formant and envelope curves. */
    controlRate: z.number().int().min(50).max(1000),
  }),
  /** Stress 1 raises the rate, pitch, pitch range and level by these fractions. */
  stress: z.object({ rate: z.number().min(0).max(2), pitch: z.number().min(0).max(2), range: z.number().min(0).max(4), gain: z.number().min(0).max(2) }),
  radio: z.object({
    highpassHz: hz,
    lowpassHz: hz,
    q: z.number().positive(),
    drive: z.number().min(0).max(40),
    bits: z.number().int().min(2).max(16),
    crushMix: z.number().min(0).max(1),
    voiceGain: z.number().min(0).max(4),
    static: z.object({ gain: z.number().min(0).max(1), crackles: z.number().int().min(0).max(1000), seconds: z.number().positive().max(10) }),
    squelch: z.object({ open: z.number().positive(), close: z.number().positive(), gain: z.number().min(0).max(2), lead: z.number().min(0), tail: z.number().min(0) }),
  }),
})

export type CommsConfig = z.infer<typeof commsSchema>

export function parseCommsConfig(raw: unknown): CommsConfig {
  const cfg = commsSchema.parse(raw)
  if (cfg.radio.lowpassHz <= cfg.radio.highpassHz) throw new Error('comms.json: radio.lowpassHz must exceed radio.highpassHz')
  return cfg
}

export const commsConfig: CommsConfig = parseCommsConfig(commsJson)
